import { ChartNoAxesColumn, CheckCircle2, Loader2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import type { SimulationJobKind } from '@/hooks/use-platform-simulation'
import { cn } from '@/lib/utils'

export type DataBenchJobPanelProps = {
  kind: SimulationJobKind | string
  status: string
  progress?: { done?: number; total?: number } | null
  error?: string | null
  className?: string
}

const KIND_LABELS: Record<string, string> = {
  generate: 'Generating people',
  pass1: 'Scoring',
  enrich: 'Filling profiles from CVs',
  demo_pack: 'Building scoring sample',
  alpha_pack: 'Seeding product world',
  hire_events: 'Putting people on Pipeline',
  extract_job_spec: 'Reading required skills from the job',
  haystack_pack: 'Loading haystack',
}

function statusBadge(status: string) {
  if (status === 'completed') {
    return (
      <Badge variant="successSoft">
        <CheckCircle2 className="mr-1 size-3" aria-hidden />
        Complete
      </Badge>
    )
  }
  if (status === 'failed') {
    return <Badge variant="destructiveSoft">Failed</Badge>
  }
  if (status === 'queued') {
    return <Badge variant="secondary">Queued</Badge>
  }
  return (
    <Badge variant="secondary" className="text-xs">
      <Loader2 className="mr-1 size-3 animate-spin" aria-hidden />
      Running
    </Badge>
  )
}

function dataBenchJobLabel(kind: string, status: string, done?: number, total?: number) {
  const verb = KIND_LABELS[kind] || 'Working'
  if (status === 'queued') return `${verb} (queued)`
  if (typeof done === 'number' && typeof total === 'number' && total > 0) {
    return `${verb} ${done} of ${total}`
  }
  return verb
}

export function DataBenchJobPanel({
  kind,
  status,
  progress,
  error,
  className,
}: DataBenchJobPanelProps) {
  const done = progress?.done ?? 0
  const total = progress?.total ?? 0
  const percent = status === 'completed' ? 100 : total > 0 ? Math.min(100, (done / total) * 100) : 0
  const text = dataBenchJobLabel(kind, status, done, total)
  const failed = status === 'failed'

  return (
    <Card className={cn('gap-0 overflow-hidden p-0', className)}>
      <div className="flex w-full items-center px-[var(--sp-card)] py-3">
        <div className="flex min-w-0 items-center gap-2">
          <ChartNoAxesColumn className="size-4 shrink-0" aria-hidden />
          <span className="truncate text-sm font-semibold">
            {KIND_LABELS[kind] || 'Simulation job'}
          </span>
          {statusBadge(status)}
        </div>
      </div>
      <CardContent className="space-y-2 px-[var(--sp-card)] pb-[var(--sp-card)] pt-0">
        <Progress value={percent} aria-label={text} />
        <p
          className="text-sm text-muted-foreground"
          role={failed ? 'alert' : 'status'}
          aria-live={failed ? undefined : 'polite'}
        >
          {failed ? error || 'Job failed' : text}
        </p>
      </CardContent>
    </Card>
  )
}
